import React, { useState, useEffect } from 'react';
import { BPForm } from '@common/BPForm';
import { AuthField } from '../Common/AuthField';
import { Layout } from './Layout';
import { Button, Divider, Header, Message, Segment } from 'semantic-ui-react';
import * as yup from 'yup';
import { userManageClient } from '../userManageClient';
import { ChangeEmailRequest, UserEmailResponse } from '@app/api2/api';
import { BPButton } from '@/components/bp/bpbutton';

interface ChangeEmailFormValues {
    newEmail: string;
}

export const Email: React.FC = () => {
    const [emailInfo, setEmailInfo] = useState<UserEmailResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [changeError, setChangeError] = useState<string>('');
    const [successMessage, setSuccessMessage] = useState<string>('');

    const initialValues: ChangeEmailFormValues = {
        newEmail: ''
    };

    const validationSchema: yup.SchemaOf<ChangeEmailFormValues> = yup.object().shape({
        newEmail: yup.string()
            .email('Invalid email address')
            .required('New email is required')
    });

    useEffect(() => {
        loadEmailInfo();
    }, []);

    const loadEmailInfo = async () => {
        try {
            setLoading(true);
            const result = await userManageClient.getEmail();
            setEmailInfo(result);
        } catch (error) {
            console.error('Error loading email info:', error);
            setChangeError('Error loading email information.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values: ChangeEmailFormValues, { resetForm }: { resetForm: () => void }) => {
        setChangeError('');
        setSuccessMessage('');
        
        try {
            const request: ChangeEmailRequest = {
                newEmail: values.newEmail
            };

            const result = await userManageClient.changeEmail(request);

            if (result.success) {
                if (result.requiresConfirmation) {
                    setSuccessMessage('Confirmation link to change email sent. Please check your email.');
                } else {
                    setSuccessMessage('Your email has been changed.');
                    await loadEmailInfo(); // Reload to get updated data
                }
                resetForm();
            } else {
                setChangeError(result.errors?.join(', ') || 'Failed to change email.');
            }
        } catch (error) {
            console.error('Change email error:', error);
            setChangeError('An error occurred while changing your email. Please try again.');
        }
    };

    if (loading) {
        return (
            <Layout>
                <Header as="h1">Manage email</Header>
                <Segment loading>
                    <div style={{ height: '200px' }} />
                </Segment>
            </Layout>
        );
    }

    if (!emailInfo) {
        return (
            <Layout>
                <Header as="h1">Manage email</Header>
                <Message error>
                    Unable to load email information.
                </Message>
            </Layout>
        );
    }

    return (
        <Layout>
            <Header as="h1">Manage email</Header>
            <p>Change your account email address</p>
            
            <Segment>
                <div style={{ marginBottom: '1rem' }}>
                    <strong>Current email:</strong> {emailInfo.email}
                    {emailInfo.isEmailConfirmed ? (
                        <Message success compact style={{ marginLeft: '1rem', display: 'inline-block' }}>
                            ✓ Verified
                        </Message>
                    ) : (
                        <Message warning compact style={{ marginLeft: '1rem', display: 'inline-block' }}>
                            ⚠ Unverified
                        </Message>
                    )}
                </div>

                <Divider />

                {successMessage && (
                    <Message success>
                        {successMessage}
                    </Message>
                )}

                {changeError && (
                    <Message error>
                        {changeError}
                    </Message>
                )}

                <BPForm
                    initialValues={initialValues}
                    vaildationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    hideButtons={true}
                >
                    {(formProps) => (
                        <>
                            <AuthField
                                name="newEmail"
                                label="New email"
                                type="email"
                                autoComplete="email"
                                placeholder="Enter your new email address"
                            />
                            
                            <Divider />
                            
                            <BPButton 
                                type="submit" 
                                primary 
                                size="large" 
                                disabled={formProps.isSubmitting}
                                loading={formProps.isSubmitting}
                            >
                                Change email
                            </BPButton>
                        </>
                    )}
                </BPForm>
            </Segment>
        </Layout>
    );
};